import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "./AdminLayout";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-HTTPS
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="text-xs h-7 px-2"
    >
      {copied ? "복사됨!" : "프롬프트 복사"}
    </Button>
  );
}

function PromptModal({ prompt, onClose }: { prompt: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-background border rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">최종 프롬프트 (Full)</h3>
          <div className="flex items-center gap-2">
            <CopyButton text={prompt} />
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 px-2">
              닫기
            </Button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed text-foreground/90">
            {prompt}
          </pre>
        </div>
        <div className="p-3 border-t text-xs text-muted-foreground">
          길이: {prompt.length}자
        </div>
      </div>
    </div>
  );
}

export default function AdminAiDebug() {
  const { data, isLoading, error } = trpc.admin.aiDebug.useQuery();
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-red-500">오류: {error.message}</div>
        </div>
      </AdminLayout>
    );
  }

  const { config, stats, recentLogs } = data!;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold">AI Image Generation Debug</h1>

        {/* Config Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Provider 설정</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">기본 Provider</div>
                <Badge variant="default" className="mt-1">
                  {config.provider}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">OpenAI Key</div>
                <Badge variant={config.openaiKeySet ? "default" : "destructive"} className="mt-1">
                  {config.openaiKeySet ? "설정됨" : "미설정"}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Forge URL</div>
                <Badge variant={config.forgeUrlSet ? "default" : "secondary"} className="mt-1">
                  {config.forgeUrlSet ? "설정됨" : "미설정"}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Forge Key</div>
                <Badge variant={config.forgeKeySet ? "default" : "secondary"} className="mt-1">
                  {config.forgeKeySet ? "설정됨" : "미설정"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.successRate}%</div>
              <div className="text-sm text-muted-foreground">성공률</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">총 요청</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">
                {stats.avgDuration ? `${(stats.avgDuration / 1000).toFixed(1)}s` : "-"}
              </div>
              <div className="text-sm text-muted-foreground">평균 생성 시간</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.fallbacks}</div>
              <div className="text-sm text-muted-foreground">Fallback 발생</div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Logs with Full Prompt */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">최근 생성 로그 (20건) — 프롬프트 전체 확인 가능</CardTitle>
          </CardHeader>
          <CardContent>
            {recentLogs.length === 0 ? (
              <div className="text-muted-foreground text-sm">아직 생성 기록이 없습니다.</div>
            ) : (
              <div className="space-y-3">
                {recentLogs.map((log: any) => (
                  <div key={log.id} className="border rounded-lg p-3 space-y-2">
                    {/* Header row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString("ko-KR")}
                      </span>
                      <Badge variant="outline" className="text-xs">{log.provider}</Badge>
                      <Badge variant="outline" className="text-xs">{log.mode}</Badge>
                      <Badge
                        variant={log.status === "success" ? "default" : "destructive"}
                        className="text-xs"
                      >
                        {log.status}
                      </Badge>
                      {log.durationMs && (
                        <span className="text-xs text-muted-foreground">
                          {(log.durationMs / 1000).toFixed(1)}s
                        </span>
                      )}
                      {log.fallbackUsed && (
                        <Badge variant="secondary" className="text-xs">Fallback</Badge>
                      )}
                    </div>

                    {/* Prompt preview */}
                    {log.finalPrompt && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">최종 프롬프트:</span>
                          <CopyButton text={log.finalPrompt} />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7 px-2"
                            onClick={() => setSelectedPrompt(log.finalPrompt)}
                          >
                            전체 보기
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            ({log.finalPrompt.length}자)
                          </span>
                        </div>
                        <div className="bg-muted/50 rounded p-2 text-xs font-mono leading-relaxed max-h-24 overflow-hidden relative">
                          {log.finalPrompt.slice(0, 300)}
                          {log.finalPrompt.length > 300 && (
                            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/80 to-transparent" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error */}
                    {log.errorMessage && (
                      <div className="text-xs text-red-500 bg-red-50 dark:bg-red-950/20 rounded p-2">
                        {log.errorMessage}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Full Prompt Modal */}
      {selectedPrompt && (
        <PromptModal prompt={selectedPrompt} onClose={() => setSelectedPrompt(null)} />
      )}
    </AdminLayout>
  );
}
